/**
 * Linear Copilot's tool handlers — what the host calls over the direct bridge. Each tool's schema,
 * title and policy live in `contract.ts` (build time only: build.mjs generates the `.well-known`
 * manifest from it, and the host validates every call against that manifest). This file takes only
 * the contract's TYPES, so zod never reaches the bundle; a handler the manifest does not list, or a
 * tool with no handler, fails the type check (`HANDLERS` is keyed by the contract's names).
 *
 * Each tool is a thin wrapper over `frontend/actions.js` and `frontend/linear.js`, read through
 * their window globals so the bundle and the unbundled app share one copy of their state.
 */

import type { McpResult, ToolContext } from "@talkie/agentic-app-transport";
import type { ToolArgs, ToolName } from "./contract";
import { cancelledByThePerson } from "../../shared/outcome.mjs";

// ─────────────────────────── Shared linear globals ──

// Surface of `linear.js` we actually call from the bridge. Keep
// permissive — the underlying client is a Linear GraphQL adapter
// whose return shapes are interface-rich; we don't reflect that
// surface verbatim in TS, just enough to type the call sites.
interface LinearApi {
  whoami: () => Promise<unknown>;
  teams: () => Promise<Array<{ id: string; key: string; name: string }>>;
  summaryEnvelope: (data: unknown) => unknown;
  listEnvelope: (data: unknown) => unknown;
  openIssue: (ref: string) => Promise<IssueDetail>;
  issueEnvelope: (data: unknown) => unknown;
  triageEnvelope: (data: unknown) => unknown;
  searchEnvelope: (data: unknown) => unknown;
  takeLastCreate: () => unknown;
  beginCreateConfirmation: () => Promise<unknown>;
  rejectPendingCreate: (code?: string, message?: string) => boolean;
}

interface IssueDetail {
  identifier?: string;
  title?: string;
  url?: string;
}

interface PreparedWrite {
  issue?: { identifier?: string };
  [k: string]: unknown;
}

/** Who asked for a view: Dobby's tools pass "dobby", so the page or card says Dobby opened it. */
interface ActionOpts { by?: "dobby" }
interface LinearActions {
  read: (tool: string, args?: unknown, opts?: ActionOpts) => Promise<unknown>;
  propose: (action: string, args: unknown, opts?: ActionOpts) => Promise<PreparedWrite>;
  draft: (args: unknown, opts?: ActionOpts) => unknown[];
  expireDrafts: () => boolean;
}

declare global {
  interface Window {
    __linearApi?: LinearApi;
    __linearActions?: LinearActions;
  }
}

function requireLinear(): LinearApi {
  if (!window.__linearApi) {
    throw new Error(
      "linear bridge: window.__linearApi not yet exported — linear.js must load before bridge.bundle.js",
    );
  }
  return window.__linearApi;
}

function requireActions(): LinearActions {
  if (!window.__linearActions) {
    throw new Error("linear bridge: window.__linearActions not yet exported — actions.js must load before bridge.bundle.js");
  }
  return window.__linearActions;
}

// ─────────────────────────── Tool → view (actions.js) ──

/**
 * Run a read through actions.js (which shows it as a new page in the app's history, noted as opened
 * by Dobby), then give Dobby the compact envelope.
 */
async function runRead(tool: string, args: unknown, enveloper: (data: unknown) => unknown): Promise<unknown> {
  return enveloper(await requireActions().read(tool, args, { by: "dobby" }));
}

/**
 * 1C write tool — NEVER mutates Linear. actions.js shows the confirm card; the write fires
 * only from the card's Confirm click (the card IS the HITL gate).
 */
async function propose(action: string, args: unknown): Promise<unknown> {
  const prep = await requireActions().propose(action, args, { by: "dobby" });
  return {
    status: "confirm_shown",
    done: false,
    applied: false,
    ref: prep.issue?.identifier ?? null,
    note:
      "NOT done yet. An editable confirm card is now shown to the user; " +
      "the change is applied to Linear ONLY when they click Confirm. Do " +
      "NOT tell the user it succeeded / was updated / changed — say you " +
      "have prepared it and they must confirm in the panel.",
  };
}

/**
 * Stack a draft on the create card, then wait for the user's Create (or cancel).
 *
 * ⛔ THE CARD MUST NOT OUTLIVE THE CALL. When the host stops waiting for this call (the person's
 * 10 minutes ran out, or the run ended: the bridge aborts `signal`) Dobby reports that nothing was
 * created, so the card stops offering Create. Without this a late Create made RES-36, an issue Dobby
 * had just said did not exist.
 */
async function proposeCreate(args: unknown, signal: AbortSignal): Promise<unknown> {
  requireActions().draft(args, { by: "dobby" });
  const settled = requireLinear().beginCreateConfirmation();
  const expire = () => {
    requireLinear().rejectPendingCreate("expired", "Dobby stopped waiting for this Linear draft; nothing was created.");
    requireActions().expireDrafts();
  };
  if (signal.aborted) expire();
  signal.addEventListener("abort", expire, { once: true });
  let result: { batch?: { created: unknown[]; failed: unknown[] } } | null;
  try {
    result = (await settled) as typeof result;
  } finally {
    signal.removeEventListener("abort", expire);
  }
  // A batch may succeed only in part; say so rather than reporting every draft as created.
  const failed = result?.batch?.failed.length ?? 0;
  const created = result?.batch ? result.batch.created.length > 0 : true;
  return { status: failed ? "partially_created" : "created", done: true, created, result };
}

function structuredErr(code: string, message: string): Error {
  const err = new Error(message);
  (err as Error & { code?: string }).code = code;
  return err;
}

// ─────────────────────────── share_issue_link (multi-step) ──

interface ShareIssueArgs {
  ref?: string;
  recipient?: string;
  target_kind?: "user" | "channel";
  message?: string;
}

async function shareIssueLink(a: ShareIssueArgs): Promise<unknown> {
  const ref = (a.ref || "").trim();
  if (!ref) {
    throw structuredErr(
      "missing_ref",
      "ref required (Linear identifier like ENG-885 or UUID).",
    );
  }
  const recipientQuery = (a.recipient || "").trim();
  if (!recipientQuery) {
    throw structuredErr(
      "missing_recipient",
      "recipient required (Talkie user display name or channel name).",
    );
  }
  const targetKind: "user" | "channel" =
    a.target_kind === "channel" ? "channel" : "user";

  // 1. Fetch the issue to get its canonical URL + identifier + title. `linear.openIssue`
  // already wraps an error with a structured `code` (`not_connected` / `not_found` / etc);
  // it propagates verbatim so Dobby can reason about it.
  const issue: IssueDetail = await requireLinear().openIssue(ref);
  if (!issue?.url) {
    throw structuredErr("no_url", `Issue ${ref} resolved but has no public URL.`);
  }

  const titleLine = issue.title
    ? `${issue.identifier ?? ref}: ${issue.title}`
    : issue.identifier ?? ref;
  const prefix =
    typeof a.message === "string" && a.message.trim().length > 0
      ? `${a.message.trim()}\n\n`
      : "";
  const content = `${prefix}${titleLine}\n${issue.url}`;

  return {
    status: "workflow_intent",
    intent: {
      kind: "talkie.share_issue_link",
      target_kind: targetKind,
      recipient: recipientQuery,
      message: content,
      issue_url: issue.url,
    },
    issue: {
      identifier: issue.identifier ?? ref,
      title: issue.title ?? "",
      url: issue.url,
    },
    recipient: {
      kind: targetKind,
      query: recipientQuery,
    },
    view: `prepared ${issue.identifier ?? ref} for ${targetKind} ${recipientQuery}`,
  };
}

// Helper: wrap tool data into an MCP tool result. The structuredContent field is the
// machine-readable channel; the text block is only a human-readable mirror.
function toToolResult(value: unknown): McpResult {
  const text = typeof value === "string" ? value : JSON.stringify(value ?? null);
  const structured = value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : undefined;
  return { ...(structured ? { structuredContent: structured } : {}), content: [{ type: "text", text }] };
}

// ─────────────────────────── Handlers ──

type Handler<K extends ToolName> = (args: ToolArgs<K>, ctx: ToolContext) => McpResult | Promise<McpResult>;

const proposeOnly = (action: string) => async (a: unknown) => toToolResult(await propose(action, a));

/** One handler per manifest tool. A failure the app throws becomes the call's `isError` result. */
export const HANDLERS: { readonly [K in ToolName]: Handler<K> } = {
  whoami: async () => toToolResult(await requireLinear().whoami()),
  list_teams: async () => toToolResult({ teams: (await requireLinear().teams()).map(({ key, name }) => ({ key, name })) }),
  show_summary: async (a) => toToolResult(await runRead("show_summary", a, requireLinear().summaryEnvelope)),
  show_issues: async (a) => toToolResult(await runRead("show_issues", a, requireLinear().listEnvelope)),
  open_issue: async ({ ref }) => toToolResult(await runRead("open_issue", { ref }, requireLinear().issueEnvelope)),
  show_triage: async (a) => toToolResult(await runRead("show_triage", a, requireLinear().triageEnvelope)),
  search: async ({ text }) => toToolResult(await runRead("search", { text }, requireLinear().searchEnvelope)),
  update_status: proposeOnly("update_status"),
  assign: proposeOnly("assign"),
  set_priority: proposeOnly("set_priority"),
  comment: proposeOnly("comment"),
  triage_decide: proposeOnly("triage_decide"),
  triage_bulk: proposeOnly("triage_bulk"),
  draft_issue: async (a, { signal }) => {
    try {
      return toToolResult(await proposeCreate(a, signal));
    } catch (err) {
      // The person cancelled the create card (its Cancel, or leaving the card): an outcome, so the host
      // says "Cancelled in Linear Copilot". The host's own expiry and a newer draft stay ordinary errors.
      if ((err as { code?: string }).code === "cancelled") return cancelledByThePerson((err as Error).message);
      throw err;
    }
  },
  get_last_create_result: async () => toToolResult(requireLinear().takeLastCreate()),
  share_issue_link: async (a) => toToolResult(await shareIssueLink(a)),
};
